import { memo, useContext, useState } from 'react';
import { UserDataContext } from '../Context/Context';
import Button from '../components/button/button';
import Popup from '../components/popup/popup';
import Loading from '../components/Loading/Loading';
import styles from './privacyDashboard.module.css';
import {
  requestDataAccess,
  requestDataExport,
  requestAnonymization,
} from '../services/api';
import success from '../icons/success.svg';

const PRIVACY_ACTIONS = [
  {
    id: 'access',
    title: 'Access My Data',
    description: 'Request a copy of all personal data we hold about you.',
    icon: '📋',
    action: 'access',
  },
  {
    id: 'export',
    title: 'Export My Data',
    description: 'Download your data in a portable, machine-readable format (JSON).',
    icon: '📥',
    action: 'export',
  },
  {
    id: 'anonymize',
    title: 'Delete Account',
    description:
      'Anonymize your account. Your reviews remain but are disconnected from your identity. Personal data is permanently deleted after 2 years.',
    icon: '🗑️',
    action: 'anonymize',
    destructive: true,
  },
];

const PrivacyDashboard = () => {
  const { user } = useContext(UserDataContext);
  const [popup, setPopup] = useState({ visible: false, message: '', error: false, details: '' });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(null);

  const showMessage = (message, error = false, details = '') => {
    setPopup({ visible: true, message, error, details });
  };

  const handleAction = async (privacyItem) => {
    if (privacyItem.destructive) {
      setShowConfirm(privacyItem);
      return;
    }

    setLoading(true);
    try {
      let result;
      switch (privacyItem.action) {
        case 'access':
          result = await requestDataAccess();
          if (result.ok) {
            showMessage(
              'Your data access request has been fulfilled.',
              false,
              JSON.stringify(result.data?.data, null, 2)
            );
          } else {
            showMessage(result.data?.message || 'Failed to retrieve data.', true);
          }
          break;
        case 'export':
          result = await requestDataExport();
          if (result.ok && result.blob) {
            const url = window.URL.createObjectURL(result.blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = result.filename || 'autie-data-export.json';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            showMessage('Your data export has been downloaded successfully.');
          } else {
            showMessage(result.data?.message || 'Failed to export data.', true);
          }
          break;
        default:
          showMessage('Unknown action.', true);
      }
    } catch (err) {
      showMessage('An error occurred. Please try again.', true);
    }
    setLoading(false);
  };

  const handleConfirmAnonymize = async () => {
    setLoading(true);
    setShowConfirm(null);
    try {
      const result = await requestAnonymization();
      if (result.ok) {
        showMessage(
          result.data?.message ||
            'Your account has been anonymized. Your reviews remain but are disconnected from your identity. Personal data will be permanently deleted after 2 years.',
          false,
          'LOGOUT_REDIRECT'
        );
      } else {
        showMessage(result.data?.message || 'Failed to anonymize account.', true);
      }
    } catch (err) {
      showMessage('An error occurred. Please try again.', true);
    }
    setLoading(false);
  };

  if (!user?.loggined) return <Loading />;

  return (
    <div className={styles.wrapper}>
      <Popup visible={popup.visible} id={styles.message} hideLogo={true}>
        {!popup.error && <img src={success} alt="success" style={{ width: '40px', height: '40px' }} />}
        <p className={styles.popupMessage}>{popup.message}</p>
        {popup.details && (
          <pre className={styles.dataPreview}>{popup.details}</pre>
        )}
        <Button onClick={() => {
          if (popup.details === 'LOGOUT_REDIRECT') {
            // Clear tokens and redirect to home
            localStorage.removeItem('ATAccTok');
            localStorage.removeItem('ATRefTok');
            window.location.href = '/';
          } else {
            setPopup({ visible: false, message: '', error: false, details: '' });
          }
        }} style={{ marginTop: '20px' }}>
          Ok
        </Button>
      </Popup>

      {loading && (
        <div className={styles.overlay}>
          <Loading />
        </div>
      )}

      <Popup
        visible={showConfirm !== null}
        hideLogo={true}
        onClose={() => setShowConfirm(null)}
        closeBtn={true}
      >
        {showConfirm?.destructive && (
          <div className={styles.confirmModal}>
            <h2 className={styles.confirmTitle}>⚠️ Delete Account</h2>
            <p className={styles.confirmText}>{showConfirm.description}</p>
            <p className={styles.confirmWarning}>
              Your account will be anonymized immediately. Reviews are preserved but no longer
              linked to you. After 2 years, all remaining personal data will be permanently
              deleted. This cannot be undone.
            </p>
            <div className={styles.confirmActions}>
              <Button buttonType="whtBtn" onClick={() => setShowConfirm(null)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmAnonymize}>Confirm</Button>
            </div>
          </div>
        )}
      </Popup>

      <h1 className={styles.title}>Privacy Settings & Data Rights</h1>
      <p className={styles.subtitle}>
        Exercise your data privacy rights. We will process your request within 30 days.
      </p>

      <div className={styles.grid}>
        {PRIVACY_ACTIONS.map((item) => (
          <div
            key={item.id}
            className={`${styles.card} ${item.destructive ? styles.destructiveCard : ''}`}
            onClick={() => handleAction(item)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleAction(item);
            }}
          >
            <div className={styles.cardIcon}>{item.icon}</div>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>{item.title}</h3>
              <p className={styles.cardDescription}>{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.info}>
        <h3>About Your Data Rights</h3>
        <ul>
          <li>
            <strong>Right to Access</strong> — Request a copy of all personal data we hold about you.
          </li>
          <li>
            <strong>Right to Data Portability</strong> — Receive your data in a structured,
            machine-readable format.
          </li>
          <li>
            <strong>Right to Erasure</strong> — Request anonymization of your account. Reviews are
            preserved but disassociated from your identity. Personal data is permanently deleted
            after 2 years.
          </li>
        </ul>
        <p className={styles.policyLink}>
          For complete details, please read our{' '}
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default memo(PrivacyDashboard);
